import React, { useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import approachImage from '../../../assets/images/arbventure/approachmockup.webp';
import screen1 from '../../../assets/images/arbventure/approachone.webp';
import screen2 from '../../../assets/images/arbventure/approachtwo.webp';
import screen3 from '../../../assets/images/arbventure/approachthree.webp';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

// ─── Types ───────────────────────────────────────────────────────────────────

type SlotName = 'laptop' | 'top' | 'right';

type ScreenItem = {
  id: number;
  image: string;
  slot: SlotName;
};

const slotStyles: Record<SlotName, React.CSSProperties & { opacity: number }> = {
  laptop: {
    left:    '5.5%',
    top:     '38%',
    width:   '45%',
    height:  '52%',
    opacity: 1,
    zIndex:  3,
  },
  top: {
    left:    '26%',
    top:     '-1%',
    width:   '47%',
    height:  '54.7%',
    opacity: 1,
    zIndex:  99,
  },
  right: {
    left:    '53%',
    top:     '36.5%',
    width:   '47%',
    height:  '55%',
    opacity: 1,
    zIndex:  999,
  },
};

// Slot rotation order: laptop → top → right → laptop → …
const NEXT_SLOT: Record<SlotName, SlotName> = {
  laptop: 'right',
  right: 'top',
  top: 'laptop',
};

const INITIAL_SCREENS: ScreenItem[] = [
  { id: 1, image: screen1, slot: 'laptop' },
  { id: 2, image: screen2, slot: 'top'    },
  { id: 3, image: screen3, slot: 'right'  },
];

// ─── MockupWithScreens ───────────────────────────────────────────────────────

const MockupWithScreens = () => {
  const [screens, setScreens] = useState<ScreenItem[]>(INITIAL_SCREENS);

  useEffect(() => {
    const id = setInterval(() => {
      setScreens(prev =>
        prev.map(screen => ({
          ...screen,
          slot: NEXT_SLOT[screen.slot],
        })),
      );
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', lineHeight: 0 }}>

      {/* Original mockup frame — sits on top via z-index so bezel covers screen edges */}
      <img
        src={approachImage}
        alt="ARB Venture dashboard-first web experience"
        style={{ position: 'relative', zIndex: 10, width: '100%', height: 'auto', display: 'block' }}
      />

      {/* Animated screens — behind the frame */}
      {screens.map(screen => {
        const target = slotStyles[screen.slot];

        return (
          <motion.div
            key={screen.id}
            animate={{
              left:    target.left,
              top:     target.top,
              width:   target.width,
              height:  target.height,
              opacity: target.opacity,
              zIndex:  target.zIndex,
            }}
            transition={{
              duration: 1.1,
              ease: [0.42, 1, 0.36, 1],
            }}
            style={{
              position:   'absolute',
              overflow:   'hidden',
              // initial position so first render is correct
              left:    slotStyles[screen.slot].left,
              top:     slotStyles[screen.slot].top,
              width:   slotStyles[screen.slot].width,
              height:  slotStyles[screen.slot].height,
            }}
          >
            <img
              src={screen.image}
              alt="ARB Venture dashboard screen"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </motion.div>
        );
      })}

    </div>
  );
};


const ApproachSection = () => {
  const { arbventureApproach } = Constants;

  const bottomUpVariant: Variants = {
    hidden: { opacity: 0, y: 80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  const imageVariant: Variants = {
    hidden: { opacity: 0, scale: 0.75 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: 'easeIn' },
    },
  };

  const leftCardVariant: Variants = {
    hidden: { opacity: 0, x: -150 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeIn', delay: i * 0.15 },
    }),
  };

  const rightCardVariant: Variants = {
    hidden: { opacity: 0, x: 150 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeIn', delay: i * 0.15 },
    }),
  };

  return (
    <section className="relative overflow-hidden pb-18 pt-8 md:pb-24 md:pt-10 lg:pb-30 2xl:pb-38">
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] h-[12rem] w-[12rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[68px] md:h-[16rem] md:w-[16rem] md:blur-[78px] lg:h-[20rem] lg:w-[20rem] lg:blur-[88px]"
        style={{ backgroundColor: 'rgba(111,175,56,0.12)' }}
      />

      <div className="relative z-10 mx-auto w-full max-w-[1260px] px-5 text-center md:px-8 lg:px-12 xl:max-w-[1420px] 2xl:max-w-[1980px] 2xl:px-16 ">

        {/* BADGE — bottom to top + continuous pulse */}
        <motion.div
          variants={bottomUpVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          <motion.div
            className="inline-flex items-center justify-center px-6 py-3 lg:px-7 lg:py-3.5 2xl:px-9 2xl:py-4 min-[2560px]:px-12 min-[2560px]:py-6"
            style={{ backgroundColor: colors.arbVentureAccent }}
          >
            <span className="font-gilroy-bold text-[2rem] font-semibold leading-none text-white lg:text-[2.35rem] xl:text-[2.7rem] 2xl:text-[3.25rem] min-[2056px]:text-[3.8rem] min-[2560px]:text-[4.8rem]">
              {arbventureApproach.badgeText}
            </span>
          </motion.div>
        </motion.div>

        {/* DESCRIPTION — bottom to top */}
        <motion.p
          variants={bottomUpVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="mx-auto mt-5 2xl:mb-20 xl:mb-10 font-gilroy-medium opacity-[80%] text-[1.05rem] leading-[1.5] md:text-[1.16rem] lg:max-w-[85%] lg:text-[1.24rem] 2xl:text-[2rem] xl:text-full 2xl:max-w-full min-[2560px]:mt-10"
        >
          {arbventureApproach.description}
        </motion.p>

        {/* IMAGE — same motion wrapper, only inner content changed */}
        <motion.div
          variants={imageVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="relative mx-auto mt-6 w-full max-w-[680px] md:mt-8 lg:max-w-[800px] xl:max-w-[900px] 2xl:max-w-[80%]"
        >
          {/* Glow layers — unchanged */}
          <div
            className="pointer-events-none absolute left-1/2 top-[44%] z-0 h-[52%] w-[74%] -translate-x-1/2 -translate-y-1/2 blur-[72px] md:h-[56%] md:w-[78%] md:blur-[84px] lg:blur-[96px]"
            style={{
              backgroundImage:
                'radial-gradient(circle at center, rgba(95,153,55,0.54) 0%, rgba(95,153,55,0.3) 34%, rgba(95,153,55,0.14) 58%, rgba(95,153,55,0) 80%)',
            }}
          />
          <div
            className="pointer-events-none absolute bottom-[8%] left-1/2 z-0 h-[30%] w-[82%] -translate-x-1/2 blur-[56px] md:h-[3 4%] md:w-[86%] md:blur-[64px]"
            style={{
              backgroundImage:
                'radial-gradient(ellipse at center, rgba(95,153,55,0.24) 0%, rgba(95,153,55,0.11) 44%, rgba(95,153,55,0) 72%)',
            }}
          />

          {/* ↓ Only this changed vs original */}
          <div className="relative z-10 drop-shadow-[0_28px_55px_rgba(0,0,0,0.45)]">
            <MockupWithScreens />
          </div>
        </motion.div>

        {/* CARDS — left cols from left, right cols from right — unchanged */}
        <div className="mx-auto mt-10 grid  grid-cols-1 gap-5 text-left md:mt-14 md:grid-cols-2 md:gap-x-7 md:gap-y-6 lg:max-w-[980px] xl:max-w-[1040px] 2xl:max-w-[85%] 2xl:gap-x-9 2xl:gap-y-8 min-[2560px]:max-w-[1600px] min-[2560px]:gap-x-14 min-[2560px]:gap-y-12 min-[2560px]:mt-20">
          {arbventureApproach.items.map((item, index) => {
            const isLeftCol = index % 2 === 0;
            const rowIndex  = Math.floor(index / 2);

            return (
              <motion.div
                key={item}
                custom={rowIndex}
                variants={isLeftCol ? leftCardVariant : rightCardVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.3 }}
                className="relative overflow-hidden rounded-[18px] p-[1.5px] shadow-[0_18px_34px_rgba(0,0,0,0.22)] md:min-h-[126px] lg:min-h-[138px] xl:min-h-[150px] 2xl:min-h-[180px] min-[2560px]:min-h-[260px] min-[2560px]:rounded-[28px]"
                style={{
                  background:
                    'linear-gradient(170deg, rgba(95,153,55,0.9) 0%, rgba(95,153,55,0.38) 20%, rgba(95,153,55,0.12) 42%, rgba(95,153,55,0.03) 72%, rgba(95,153,55,0) 100%)',
                }}
              >
                <div
                  className="relative h-full w-full rounded-[17px] px-6 py-15 md:px-7 2xl:px-9 min-[2560px]:px-14 min-[2560px]:py-10 min-[2560px]:rounded-[27px]"
                  style={{
                    backgroundImage: `
                      radial-gradient(rgba(95,153,55,0) 68%),
                      linear-gradient(180deg, rgba(17,24,17,0.98) 0%, rgba(12,16,12,0.99) 52%, rgba(8,11,8,1) 100%)
                    `,
                  }}
                >
                  <span
                    className="pointer-events-none absolute left-6 top-3 bg-clip-text font-gilroy-bold text-[4.4rem] font-bold leading-none text-transparent md:left-7 md:top-4 md:text-[5rem] xl:text-[5.8rem] 2xl:text-[7rem] min-[2560px]:text-[10rem] min-[2560px]:left-10 min-[2560px]:top-6"
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg, rgba(95, 153, 55, 0.32) 0%, rgba(95, 153, 55, 0.08) 55%, rgba(95, 153, 55, 0) 100%)',
                    }}
                    aria-hidden="true"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p className="absolute bottom-6 left-6 right-6 z-10 font-gilroy-semibold text-[1rem] leading-[1.35] md:left-7 md:right-7 md:text-[1.08rem] lg:text-[1.15rem] xl:bottom-7 xl:text-[1.24rem] 2xl:left-9 2xl:right-9 2xl:text-[1.48rem] min-[2560px]:text-[2.2rem] min-[2560px]:bottom-10 min-[2560px]:left-14 min-[2560px]:right-14">
                    {item}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ApproachSection;